import { useState } from "react";
import { WorkforceComment } from "@/types/workforce";
import { CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PlusCircle, Trash2, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getUserDisplayName } from "@/utils/userUtils";
import { AddCommentModal } from "@/components/candidates/AddCommentModal";
import { CommentContent } from "@/components/comments/CommentContent";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";

interface WorkforceCommentsProps {
	comments: WorkforceComment[];
	onAddComment: (content: string) => Promise<void>;
	onDeleteComment: (commentId: string) => Promise<void>;
	onUpdateComment: (commentId: string, content: string) => Promise<void>;
}

export const WorkforceComments = ({
	comments,
	onAddComment,
	onDeleteComment,
	onUpdateComment,
}: WorkforceCommentsProps) => {
	const { t } = useTranslation();
	const [isAddCommentOpen, setIsAddCommentOpen] = useState(false);
	const [commentToDelete, setCommentToDelete] = useState<WorkforceComment | null>(null);
	const [commentToEdit, setCommentToEdit] = useState<WorkforceComment | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	// Pagination state
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 4;

	// Calculate pagination
	const totalPages = Math.ceil(comments.length / itemsPerPage);
	const indexOfLastComment = currentPage * itemsPerPage;
	const indexOfFirstComment = indexOfLastComment - itemsPerPage;
	const currentComments = comments
		.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
		.slice(indexOfFirstComment, indexOfLastComment);

	const handleAddComment = async (content: string) => {
		await onAddComment(content);
		setIsAddCommentOpen(false);
	};

	const handleEditComment = async (content: string) => {
		if (!commentToEdit) return;
		await onUpdateComment(commentToEdit.id, content);
		setCommentToEdit(null);
	};

	const handleDeleteComment = async () => {
		if (!commentToDelete) return;

		try {
			setIsDeleting(true);
			await onDeleteComment(commentToDelete.id);
			setCommentToDelete(null);
		} finally {
			setIsDeleting(false);
		}
	};

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		const scrollableParent = document.querySelector('.overflow-y-auto') as HTMLElement;
		if (scrollableParent) {
			scrollableParent.style.scrollBehavior = 'auto';
			scrollableParent.scrollTop = 0;
			scrollableParent.style.scrollBehavior = '';
		}
	};

	return (
		<div className="bg-white rounded-sm shadow">
			<CardContent className="p-6">
				<div className="flex justify-between items-center mb-6">
					<h3 className="text-lg font-semibold">{t("workforce.tabs.comments", "Comments")}</h3>
					<Button
						onClick={() => setIsAddCommentOpen(true)}
						className="flex items-center gap-1"
					>
						<PlusCircle className="h-4 w-4" />
						<span>{t("companies.comments.addComment")}</span>
					</Button>
				</div>

				{comments.length === 0 ? (
					<p className="text-gray-500 italic text-center py-8">{t("companies.comments.noComments", "No comments yet")}</p>
				) : (
					<div className="space-y-4">
						{currentComments.map((comment) => (
							<div key={comment.id} className="bg-gray-50 p-4 rounded-sm">
								<div className="flex justify-between mb-2">
									<span className={`font-medium ${getUserDisplayName(comment).isItalic ? 'italic' : ''}`}>
										{getUserDisplayName(comment).name}
									</span>
									<div className="flex items-center gap-2">
										<span className="text-sm text-gray-500">
											{new Date(comment.created_at).toLocaleString()}
										</span>
										<Button
											variant="ghost"
											size="sm"
											onClick={() => setCommentToEdit(comment)}
											className="h-6 w-6 p-0 text-gray-400 hover:text-blue-500"
										>
											<Pencil className="h-4 w-4" />
										</Button>
										<Button
											variant="ghost"
											size="sm"
											onClick={() => setCommentToDelete(comment)}
											className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
										>
											<Trash2 className="h-4 w-4" />
										</Button>
									</div>
								</div>
								<CommentContent content={comment.content} />
							</div>
						))}

						{/* Pagination */}
						{totalPages > 1 && (
							<div className="sticky bottom-0 z-10 bg-white">
								<Pagination>
									<PaginationContent>
										<PaginationItem>
											<PaginationPrevious
												onClick={() => handlePageChange(currentPage - 1)}
												className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
											/>
										</PaginationItem>

										{Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
											<PaginationItem key={page}>
												<PaginationLink
													isActive={page === currentPage}
													onClick={() => handlePageChange(page)}
													className="cursor-pointer"
												>
													{page}
												</PaginationLink>
											</PaginationItem>
										))}

										<PaginationItem>
											<PaginationNext
												onClick={() => handlePageChange(currentPage + 1)}
												className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
											/>
										</PaginationItem>
									</PaginationContent>
								</Pagination>
							</div>
						)}
					</div>
				)}

				<AddCommentModal
					isOpen={isAddCommentOpen}
					onClose={() => setIsAddCommentOpen(false)}
					onSave={handleAddComment}
				/>

				<AddCommentModal
					isOpen={!!commentToEdit}
					onClose={() => setCommentToEdit(null)}
					onSave={handleEditComment}
					initialContent={commentToEdit?.content || ""}
					isEditing={true}
				/>

				<AlertDialog open={!!commentToDelete} onOpenChange={(open) => !open && setCommentToDelete(null)}>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>Delete Comment</AlertDialogTitle>
							<AlertDialogDescription>
								Are you sure you want to delete this comment? This action cannot be undone.
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
							<AlertDialogAction
								onClick={handleDeleteComment}
								disabled={isDeleting}
								className="bg-red-600 hover:bg-red-700"
							>
								{isDeleting ? "Deleting..." : "Delete"}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>
			</CardContent>
		</div>
	);
};
